import { Component } from '@angular/core';
import { ScenarioComponent } from './scenario/scenario.component';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { ResultsComponent } from './results/results.component';
import {
  CarePathwayStep,
  ConfigItem,
  IntroInfo,
  ResultCategory,
  ResultDataService,
  ScenarioCategory,
  SummaryItem,
  TimeHorizonKey,
} from '../../services/result-data/result-data.service';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { OrchestratorService } from '../../services/orchestrator/orchestrator.service';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription, switchMap } from 'rxjs';
import { FileService } from '../../services/file/file.service';
import { ERROR_SNACKBAR_CONFIG } from '../../constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  trigger,
  transition,
  style,
  animate,
  AnimationEvent,
} from '@angular/animations';
import { FileMetadata } from '../../models/file-metadata.model';
import { MatSidenavModule } from '@angular/material/sidenav';
import { IntroMenuComponent } from './intro-menu/intro-menu.component';
import { IntroStateService } from '../../services/intro-state/intro-state.service';
import { MatDialog } from '@angular/material/dialog';
import { ScreenWarningDialogComponent } from './screen-warning-dialog/screen-warning-dialog.component';

@Component({
  selector: 'app-dashboard',
  imports: [
    ScenarioComponent,
    ReactiveFormsModule,
    MatInputModule,
    ResultsComponent,
    MatButtonModule,
    CommonModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    IntroMenuComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  animations: [
    trigger('spinnerAnimation', [
      transition(':leave', [
        style({ transform: 'translateY(0)' }),
        animate('300ms ease-out', style({ transform: 'translateY(100%)' })),
      ]),
    ]),
  ],
})
export class DashboardComponent {
  resultCategories: ResultCategory[] = [];
  carePathwaySteps: CarePathwayStep[] = [];
  timeHorizon: TimeHorizonKey = '';
  scenarioCategories: ScenarioCategory[] = [];
  introInfos: IntroInfo[] = [];
  configItems: ConfigItem[] = [];
  fileMetadata!: FileMetadata;
  summaryItems: SummaryItem[] = [];

  loadingSpinnerActive = true;
  appContentVisible = false;
  introActive = true;

  private testModelMetadata: FileMetadata = {
    id: '0',
    modelName: 'Modèle de test',
    modelVersion: '0',
    fileName: '',
    downloadUrl: '',
    uploadedAt: { _nanoseconds: 0, _seconds: 0 },
    version: '0',
  };

  private _subs: Subscription[] = [];

  constructor(
    private readonly _resultDataService: ResultDataService,
    private readonly _orchestrator: OrchestratorService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _fileService: FileService,
    private readonly _snackBar: MatSnackBar,
    private readonly _introStateService: IntroStateService,
    private readonly _dialog: MatDialog
  ) {}

  ngOnInit() {
    const subInit = this._route.queryParams
      .pipe(
        switchMap((params) =>
          params['id']
            ? this._fileService.getExcelFileById(params['id'])
            : of(undefined)
        ), // first async op
        switchMap((result) => {
          this.fileMetadata = result ?? this.testModelMetadata;
          return this._orchestrator.initialize(result?.id ?? '');
        }) // second async op using result.id
      )
      .subscribe({
        next: () => {
          // Orchestrator initialized successfully
          this.loadingSpinnerActive = false;
          this._dialog.open(ScreenWarningDialogComponent);
        },
        error: (error) => {
          this._snackBar.open(
            this.fileMetadata?.id == this.testModelMetadata.id
              ? 'Erreur dans la visualisation du modèle. Si le problème persiste, contactez le support.'
              : error,
            'Fermer',
            ERROR_SNACKBAR_CONFIG
          );
        },
      });

    const subResultCategories =
      this._resultDataService.resultCategories$.subscribe((value) => {
        this.resultCategories = value;
      });

    const subCarePathwaySteps =
      this._resultDataService.carePathwaySteps$.subscribe((value) => {
        this.carePathwaySteps = value;
      });

    const subTimeHorizon = this._resultDataService.timeHorizon$.subscribe(
      (value) => {
        this.timeHorizon = value;
      }
    );

    const subScenarioCategories =
      this._resultDataService.scenarioCategories$.subscribe((value) => {
        this.scenarioCategories = value;
      });

    const subIntroInfos = this._resultDataService.introInfos$.subscribe(
      (value) => {
        this.introInfos = value;
      }
    );

    const subConfigItems = this._resultDataService.configItems$.subscribe(
      (value) => {
        this.configItems = value;
      }
    );

    const subSummary = this._resultDataService.summaryItems$.subscribe(
      (value) => {
        this.summaryItems = value;
      }
    );

    const introState = this._introStateService.activeState$.subscribe(
      (value) => {
        this.introActive = value;
      }
    );

    this._subs.push(
      subInit,
      subResultCategories,
      subCarePathwaySteps,
      subTimeHorizon,
      subScenarioCategories,
      subIntroInfos,
      subConfigItems,
      subSummary,
      introState
    );
  }

  ngOnDestroy() {
    this._subs.forEach((s) => s.unsubscribe());
  }

  onSpinnerAnimationDone(event: AnimationEvent) {
    if (event.toState === 'void') {
      this.appContentVisible = true;
    }
  }

  onClickBrand() {
    this._router.navigate(['/']);
  }
}
